import { Icon } from '@iconify/react'
import {
  Dispatch,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import useGetSessions from 'src/apis/useGetSessions'
import { CAI_CHAT_FLOWS } from 'src/constants/cai-chat.constant'
import { Item, ItemStatus } from 'src/types/session.type'
import styled from 'styled-components'
import { CaiPopupContext } from '../../context/CaiPopupContext'
import { NotificationContext } from '../../context/NotificationContext'
import dayjs from 'dayjs'

type Props = {
  newChat: (selectedFlow: string) => void
  setMessagesMap: Dispatch<SetStateAction<any>>
}

const NotificationItem = styled('li')({
  '&:hover': { cursor: 'pointer', backgroundColor: '#ddd' },
  '&:not(:last-child)': { borderBottom: '1px solid #bbb !important' },
})

function Notification({ newChat, setMessagesMap }: Props) {
  const { t } = useTranslation()
  const { sessionId, setSessionId, handleCompleteActivity, setIsInActive } =
    useContext(NotificationContext)

  const [show, setShow] = useState(false)
  const { notificationRef } = useContext(CaiPopupContext)
  const { data = [] } = useGetSessions({
    startDate: dayjs().toISOString(),
  })

  const completedData = data.filter((m) => m.status === ItemStatus.completed)

  const incompleteData = data.filter((m) => m.status !== ItemStatus.completed)

  const hasUnread = data.some((m) => m.status === ItemStatus.ready)

  const handleShowToggle = () => setShow((state) => !state)

  const handleItemClick = (item: Item) => {
    const { id, status } = item
    handleShowToggle()
    if (id === sessionId) return
    newChat(CAI_CHAT_FLOWS.SESSION)
    setSessionId(id)
    setIsInActive(status === ItemStatus.inactive)
    setMessagesMap((state: any) => ({ ...state, session: [] }))
  }

  useEffect(() => {
    if (sessionId) newChat(CAI_CHAT_FLOWS.SESSION)
  }, [])

  if (handleCompleteActivity) return null

  return (
    <OverlayTrigger
      rootClose
      placement="bottom"
      show={show}
      trigger={'click'}
      overlay={
        <Popover style={{ maxWidth: 300, width: 300 }}>
          <Popover.Header as="h3">
            {t('notification.title', 'Notification')}
          </Popover.Header>

          <Popover.Body className="p-0">
            <ul
              className="list-group rounded-bottom rounded-top-0 overflow-auto"
              style={{ maxHeight: '50vh' }}
            >
              {[...incompleteData].reverse().map((m) => (
                <NotificationItem
                  key={m.id}
                  className="list-group-item border-0 d-flex flex-column rounded-0"
                  onClick={() => handleItemClick(m)}
                >
                  <div className="fw-bold fs-6">
                    {m.title}
                    <Icon
                      className="ms-1"
                      height="10"
                      icon="material-symbols-light:circle"
                      width="10"
                    />
                  </div>

                  <div>{m.description}</div>

                  <div className="align-self-end text-muted">
                    {new Date(m.updated_at).toLocaleString()}
                  </div>
                </NotificationItem>
              ))}

              {!!completedData.length && (
                <div
                  className="fw-bold px-3"
                  style={{ borderBottom: '1px solid #bbb' }}
                >
                  {t('notification.completed', 'Completed')}
                </div>
              )}

              {[...completedData].reverse().map((m, i) => (
                <NotificationItem
                  key={m.id}
                  className="list-group-item border-0 d-flex flex-column rounded-0"
                  style={{ background: 'white' }}
                  onClick={() => handleItemClick(m)}
                >
                  <div className="fw-bold fs-6">{m.title}</div>

                  <div>{m.description}</div>

                  <div className="align-self-end text-muted">
                    {new Date(m.updated_at).toLocaleString()}
                  </div>
                </NotificationItem>
              ))}
            </ul>
          </Popover.Body>
        </Popover>
      }
      onToggle={handleShowToggle}
    >
      <Icon
        ref={notificationRef as any}
        className={hasUnread ? 'pulse-alert glow' : ''}
        height={24}
        role="button"
        style={{ color: hasUnread ? 'rgb(220, 0, 0)' : 'inherit' }} // Change color to red if unread
        width={24}
        icon={`material-symbols-light:notifications${
          hasUnread ? '-unread' : ''
        }`}
        onClick={() => document.body.click()}
      />
    </OverlayTrigger>
  )
}

export default memo(Notification)
